/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */
import * as Models from '@models';
import * as Services from '@services';
import { TenantProfile } from '@services';
import { Utils } from '@utils';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class TenantProfileStore {
  tenantProfile: Services.TenantProfile | undefined;
  workStationInUse!: Models.TenantProfileWorkStation | null;
  tenantProfileWorkStations: Models.TenantProfileWorkStation[] = [];
  availableWorkStations: Models.TenantProfileWorkStation[] = [];
  availableWorkStationsLoading = false;
  tenantProfiles: Map<number, TenantProfile> = new Map();
  totalCount: number = 0;
  loading = false;
  skipCount: number = 0;
  maxResultCount: number = 15;
  currentPage: number = 1;

  workStationsLoading = false;

  //seleted work station on calendar rescheduled modal
  selectedWorkStationId: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  handlePageChange = (pagination: any) => {
    this.maxResultCount = pagination.pageSize;
    this.setSkipCount((pagination.current - 1) * pagination.pageSize);
    this.setCurrentPage(pagination.current);
  };

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setWorkStationsLoading = (value: boolean) => {
    runInAction(() => {
      this.workStationsLoading = value;
    });
  };

  setAvailableWorkStationsLoading = (value: boolean) => {
    runInAction(() => {
      this.availableWorkStationsLoading = value;
    });
  };

  async getProfile(entityDto: Services.EntityDto) {
    this.setLoading(true);
    try {
      const result = await Services.TenantProfileService.get(entityDto);
      runInAction(() => {
        this.tenantProfile = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.log(error);
    }
  }

  async getAll(pagedFilterAndSortedRequest: Services.PagedTenantProfileResultRequestDto) {
    this.setLoading(true);
    try {
      const result = await Services.TenantProfileService.getAll(pagedFilterAndSortedRequest);
      runInAction(() => {
        this.tenantProfiles.clear();
        result.items.forEach((tenantProfile) => {
          this.tenantProfiles.set(tenantProfile.id, tenantProfile);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.log(error);
    }
  }

  async getAllWorkStations(input: Services.PagedTenantProfileWorkStations) {
    try {
      this.setWorkStationsLoading(true);
      const result = await Services.TenantProfileService.getAllWorkStations(input);
      runInAction(() => {
        this.tenantProfileWorkStations = result.items;
      });
      this.setWorkStationsLoading(false);
    } catch (error) {
      this.setWorkStationsLoading(false);
      console.log(error);
    }
  }

  //refactored
  async getAvailableWorkStations(input: Services.AvailableStationInput) {
    this.setAvailableWorkStationsLoading(true);
    try {
      const result = await Services.TenantProfileService.getAvailableWorkStations(input);
      runInAction(() => {
        this.availableWorkStations = result;
      });
      this.setAvailableWorkStationsLoading(false);
    } catch (error) {
      this.setAvailableWorkStationsLoading(false);
      console.log(error);
    }
  }

  async getWorkStationInUse(input: Services.AvailableStationInput) {
    const result = await Services.TenantProfileService.getWorkStationInUse(input);
    runInAction(() => {
      this.workStationInUse = result;
    });
    return result;
  }

  async updateProfile(tenantProfile: Services.UpdateTenantProfile) {
    try {
      const website = Utils.configureUrl(tenantProfile.website);
      const input: Services.UpdateTenantProfile = { ...tenantProfile, website };
      const result = await Services.TenantProfileService.update(input);
      runInAction(() => {
        this.tenantProfile = result;
      });
      message.success({
        content: 'Successfully updated!',
      });
      return result;
    } catch (error) {
      return null;
    }
  }

  async updateLogo(input: Services.ProfileLogoInput): Promise<string> {
    const result = await Services.TenantProfileService.updateLogo(input);
    runInAction(() => {
      this.tenantProfile!.logo = result;
    });
    return result;
  }

  async deleteLogo() {
    await Services.TenantProfileService.deleteLogo();
    runInAction(() => {
      this.tenantProfile!.logo = undefined;
    });
  }

  async onlineBookingChange() {
    const result = await Services.TenantProfileService.onlineBookingChange();
    runInAction(() => {
      this.tenantProfile!.onlineBooking = result;
    });
  }

  async updateVatNumber(input: Services.UpdateTenantProfileVatNumber) {
    try {
      const result = await Services.TenantProfileService.updateVatNumber(input);
      runInAction(() => {
        this.tenantProfile!.vatNumber = result;
      });
      message.success({
        content: 'Successfully updated!',
      });
      return result;
    } catch (error) {
      return null;
    }
  }

  async changeActive(id: number) {
    const result = await Services.TenantProfileService.changeActive(id);
    runInAction(() => {
      const tenantProfile = this.tenantProfiles.get(id);

      if (tenantProfile) {
        tenantProfile.tenant!.isActive = result.isActive;
        this.tenantProfiles.set(id, tenantProfile);
      }
    });
    message.success({
      content: `Garage ${result.name} was successfully ${result.isActive ? 'activated' : 'deactivated'}!`,
    });
  }

  addTenantProfileObservable(tenant: Services.CreateTenantInput) {
    const added: TenantProfile = {
      ...tenant.tenantProfile,
      id: tenant.tenantProfileId || 0,
      tenant: {
        id: tenant.id,
        isActive: tenant.isActive,
        name: tenant.name,
        tenancyName: '',
        tenantProfileId: 0,
        tenantProfile: undefined,
        tenantBankAccount: undefined,
      },
      tenantId: tenant.id,
    };
    runInAction(() => {
      const newTenant = added;

      // Add the new customer to the beginning of the map
      const tempArray = Array.from(this.tenantProfiles.entries());
      tempArray.unshift([newTenant.id, newTenant]);

      // If there are more than 10 tenants, remove the oldest one
      if (tempArray.length > 10) {
        tempArray.pop();
      }

      // Clear the map and add the updated list back to it
      this.tenantProfiles.clear();
      tempArray.forEach(([id, user]) => {
        this.tenantProfiles.set(id, user);
      });

      this.totalCount += 1;
    });
  }

  async updateOrCreateWorkStation(index: number) {
    try {
      const workStation = this.tenantProfileWorkStations[index];
      if (workStation.id) {
        await Services.TenantProfileService.updateWorkStation(workStation);
        this.dispatchWorkStations({
          type: Models.CHANGE_WORK_STATION,
          workStation: { ...workStation, inEdit: false },
          index,
        });
      } else {
        const result = await Services.TenantProfileService.createWorkStation({
          name: workStation.name,
          tenantProfileId: this.tenantProfile!.id,
        });
        this.dispatchWorkStations({
          type: Models.CHANGE_WORK_STATION,
          workStation: { ...workStation, ...result, inEdit: false },
          index,
        });
      }
      message.success({ content: 'Successfully updated!' });
    } catch (error) {
      console.error(error);
    }
  }

  async deleteWorkStation(entity: Services.EntityDto<string>) {
    try {
      await Services.TenantProfileService.deleteWorkStation(entity);
      runInAction(() => {
        this.tenantProfileWorkStations = this.tenantProfileWorkStations.filter(
          (x) => x.id !== entity.id
        );
      });
      message.success({ content: 'Successfully deleted!' });
    } catch (error) {
      console.error(error);
    }
  }

  dispatchWorkStations = (action: Models.WorkStationActions) => {
    switch (action.type) {
      case Models.ADD_WORK_STATION:
        runInAction(() => {
          this.tenantProfileWorkStations = [
            ...this.tenantProfileWorkStations,
            Models.InitialWorkStation,
          ];
        });
        return;
      case Models.REMOVE_WORK_STATION:
        runInAction(() => {
          this.tenantProfileWorkStations = [
            ...this.tenantProfileWorkStations.filter(
              (_workSchedule: any, i: number) => i !== action.index
            ),
          ];
        });
        return;
      case Models.CHANGE_WORK_STATION:
        const items = [...this.tenantProfileWorkStations];
        items[action.index] = { ...items[action.index], ...action.workStation };
        runInAction(() => {
          this.tenantProfileWorkStations = items;
        });
        return;
      case Models.CLEAR_WORK_STATION:
        runInAction(() => {
          this.tenantProfileWorkStations = [];
        });
        return;
      default:
        return this.tenantProfileWorkStations;
    }
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  clearTenantProfiles = () => {
    runInAction(() => {
      this.tenantProfiles.clear();
    });
  };

  clearTenantProfile = () => {
    runInAction(() => {
      this.tenantProfile = undefined;
    });
  };

  clearTenantProfileWorkStations = () => {
    runInAction(() => {
      this.tenantProfileWorkStations = [];
    });
  };

  clearAvailableWorkStations = () => {
    runInAction(() => {
      this.availableWorkStations = [];
    });
  };

  clearWorkStationInUse = () => {
    runInAction(() => {
      this.workStationInUse = null;
    });
  };

  clearSelectedWorkStation = () => {
    runInAction(() => {
      this.selectedWorkStationId = undefined;
    });
  };

  resetWorkStationInUse = () => {
    runInAction(() => {
      this.workStationInUse = null;
    });
  };
}

export default TenantProfileStore;
